import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, subscribeToCollection, unsubscribeFromCollection } from '@/lib/supabase';
import type { VideoItem } from '@/lib/supabase';
import VideoCard from '@/components/VideoCard';
import Navbar from '@/components/Navbar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Heart, Link2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';

const CollectionPage = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: collection, isLoading } = useQuery({
    queryKey: ['public-collection', id],
    queryFn: async () => {
      try {
        if (!id) return null;

        const { data: collection, error: collectionError } = await supabase
          .from('collections')
          .select('*')
          .eq('public_id', id)
          .single();

        if (collectionError || !collection) {
          console.error('Collection error:', collectionError);
          return null;
        }

        const { data: videos, error: videosError } = await supabase
          .from('collection_videos')
          .select('*')
          .eq('collection_id', collection.id)
          .order('created_at', { ascending: false });

        if (videosError) {
          console.error('Videos error:', videosError);
          return null;
        }

        // Only check for subscription if user is authenticated
        let isSubscribed = false;
        if (user?.id) {
          const { data: subscription } = await supabase
            .from('collection_subscriptions')
            .select('id')
            .eq('collection_id', collection.id)
            .eq('user_id', user.id)
            .maybeSingle();
          isSubscribed = !!subscription;
        }

        return {
          ...collection,
          videos: videos?.map(v => ({
            id: v.video_id,
            videoId: v.video_id,
            title: v.title,
            thumbnail: v.thumbnail
          })) || [],
          is_subscribed: isSubscribed
        };
      } catch (error) {
        console.error('Error fetching collection:', error);
        return null;
      }
    },
    retry: false
  });

  const subscribe = useMutation({
    mutationFn: () => subscribeToCollection(collection!.id!),
    onSuccess: () => {
      queryClient.invalidateQueries(['public-collection', id]);
      queryClient.invalidateQueries(['collections']);
      toast({
        title: "Subscribed",
        description: "You've subscribed to this collection"
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to subscribe to collection",
        variant: "destructive"
      });
    }
  });

  const unsubscribe = useMutation({
    mutationFn: () => unsubscribeFromCollection(collection!.id!),
    onSuccess: () => {
      queryClient.invalidateQueries(['public-collection', id]);
      queryClient.invalidateQueries(['collections']);
      toast({
        title: "Unsubscribed",
        description: "You've unsubscribed from this collection"
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to unsubscribe from collection",
        variant: "destructive"
      });
    }
  });

  const handleSubscribeToggle = () => {
    if (!user) {
      navigate('/auth');
      return;
    }

    if (collection?.is_subscribed) {
      unsubscribe.mutate();
    } else {
      subscribe.mutate();
    }
  };

  const handleCopyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast({
      title: "Link Copied",
      description: "Collection link copied to clipboard"
    });
  };

  const isOwner = user?.id === collection?.user_id;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="container max-w-7xl mx-auto px-4 py-16">
          <div className="animate-pulse space-y-8">
            <div className="h-8 w-1/2 bg-muted rounded"></div>
            <div className="h-4 w-2/3 bg-muted rounded"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-video bg-muted rounded"></div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="container max-w-7xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold mb-4">Collection not found</h1>
          <p className="text-muted-foreground">
            This collection doesn't exist or is no longer public.
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">{collection.name}</h1>
            {collection.description && (
              <p className="text-muted-foreground">{collection.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleCopyLink}
            >
              <Link2 size={16} className="mr-2" />
              Copy Link
            </Button>

            {!isOwner && user && (
              <Button
                variant={collection.is_subscribed ? "default" : "outline"}
                onClick={handleSubscribeToggle}
                className="min-w-[120px]"
                disabled={subscribe.isPending || unsubscribe.isPending}
              >
                <Heart
                  size={16}
                  className={`mr-2 ${collection.is_subscribed ? "fill-current" : ""}`}
                />
                {collection.is_subscribed ? "Subscribed" : "Subscribe"}
              </Button>
            )}
          </div>
        </div>

        <ScrollArea className="h-[calc(100vh-250px)]">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {collection.videos?.map((video) => (
              <VideoCard
                key={video.videoId}
                video={video}
                inCollection={false}
              />
            ))}
          </div>
        </ScrollArea>
      </main>
    </div>
  );
};

export default CollectionPage;